import type { PlanEditResume, PlanEntryEdit, RunPlan, RunPlanEntry } from "@procurement-agents/scope-api";

/** CHANGE_plan_chat: the plan the reviewer edits, client side. The same rules the API applies, so Accept is never
 *  refused by surprise and the inline message is the one the server would have sent. */

const NUMBER = /^(\d+(?:\.\d+)*)([A-Za-z]?)\.?\s+(.+)$/;
export const MAX_ENTRIES = 30;
export const MAX_FOCUS_NOTE = 1200;   // the planner's own notes run to about 550 characters
export const MAX_GUIDANCE = 2000;
export const ADHOC = /^adhoc_[a-z0-9-]{3,40}$/;
export const MIN_LIFT_REASON = 10;   // CHANGE_plan_control: the server's own minimum

export const included = (plan: RunPlan) => plan.entries.filter((e) => e.included);
export const partOf = (title: string) => NUMBER.exec(title)?.[1]?.split(".")[0] ?? null;
const strip = (title: string) => NUMBER.exec(title)?.[3] ?? title;
/** A merged section's title: it takes its first part's place, so its number too ("2.2 Invoicing and Payment"); the
 *  other parts' numbers leave the sequence and the rows after them close up. A title already numbered stays as written. */
export function numberedLike(title: string, first?: string | null): string {
  const m = NUMBER.exec(first ?? "");
  return !m || NUMBER.test(title.trim()) ? title.trim() : `${m[1]}${m[2]} ${title.trim()}`;
}
/** Where a template section sits in its schedule, for putting a part back: "2.10A" -> [2, 10, "A"]. */
const placeOf = (title: string): Array<number | string> | null => {
  const m = NUMBER.exec(title);
  return m ? [...m[1]!.split(".").map(Number), m[2] ?? ""] : null;
};
const before = (a: Array<number | string>, b: Array<number | string>) => {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? -1, y = b[i] ?? -1;
    if (x !== y) return typeof x === "number" && typeof y === "number" ? x < y : String(x) < String(y);
  }
  return false;
};

/** The plan with its entries in the order the reviewer sees: included ones in `order`, then everything left out. */
export function orderPlan(plan: RunPlan, order: string[]): RunPlan {
  const by = new Map(plan.entries.map((e) => [e.id, e]));
  const first = order.map((id) => by.get(id)).filter((e): e is RunPlanEntry => !!e && !!e.included);
  const seen = new Set(first.map((e) => e.id));
  return { ...plan, entries: [...first, ...plan.entries.filter((e) => !seen.has(e.id))] };
}

/** Numbers for the added rows (`is_adhoc`, unnumbered), from where they sit: the next item at the level of the numbered
 *  row before (2.3 -> an item of part 2), else at the level of the numbered row after (first in that part), else a
 *  part of their own between two part headings; none with no numbered neighbour. The server says the same. */
function byPosition(numbers: Array<string[] | null>, positional: boolean[]): Array<string[] | null> {
  const out = [...numbers];
  numbers.forEach((own, i) => {
    if (own || !positional[i]) return;
    const prev = out.slice(0, i).reverse().find((n) => n) ?? null;
    const after = numbers.slice(i + 1).find((n) => n) ?? null;
    if (prev && prev.length > 1) out[i] = [...prev.slice(0, -1), "0"];
    else if (after && after.length > 1) out[i] = [...after.slice(0, -1), "0"];
    else if (prev || after) out[i] = [`+${i}`];
  });
  return out;
}

export function renumber(rows: Array<{ id: string; title: string; parent?: string | null; is_adhoc?: boolean }>): string[] {
  const present = new Set(rows.map((r) => r.id));
  const isTop = (r: { parent?: string | null }) => !r.parent || !present.has(r.parent);
  const tops = rows.filter(isTop);
  const numbers = byPosition(tops.map((r) => NUMBER.exec(r.title)?.[1]?.split(".") ?? null), tops.map((r) => !!r.is_adhoc));
  const parts: string[] = [];
  for (const n of numbers) if (n && !parts.includes(n[0]!)) parts.push(n[0]!);
  const partMap = new Map(parts.map((old, i) => [old, String(i + 1)]));
  const counters = new Map<string, number>();
  const titles = new Map<string, string>();
  tops.forEach((r, i) => {
    const n = numbers[i];
    if (!n) { titles.set(r.id, r.title); return; }
    const text = strip(r.title);
    const head = partMap.get(n[0]!)!;
    if (n.length === 1) { titles.set(r.id, `${head}. ${text}`); return; }
    const prefix = [head, ...n.slice(1, -1)].join(".");
    const k = (counters.get(prefix) ?? 0) + 1;
    counters.set(prefix, k);
    titles.set(r.id, `${prefix}.${k} ${text}`);
  });
  const kids = new Map<string, number>();
  return rows.map((r) => {
    if (titles.has(r.id)) return titles.get(r.id)!;
    const prefix = NUMBER.exec(titles.get(r.parent!) ?? "")?.[1];
    if (!prefix) return strip(r.title);
    const n = (kids.get(r.parent!) ?? 0) + 1;
    kids.set(r.parent!, n);
    return `${prefix}.${n} ${strip(r.title)}`;
  });
}

export const childrenOf = (plan: RunPlan, id: string) => plan.entries.filter((e) => e.parent === id);

/** The numbers the rows read with: the body renumbered as the document will read, addenda on their own (A1, A1.1). */
export function numbered(rows: RunPlanEntry[]): Map<string, string> {
  const body = rows.filter((r) => !r.addendum);
  const out = new Map(renumber(body).map((t, i) => [body[i]!.id, t]));
  const top = new Map<string, number>();
  const kids = new Map<string, number>();
  for (const r of rows.filter((x) => x.addendum)) {
    if (r.parent && top.has(r.parent)) {
      const n = (kids.get(r.parent) ?? 0) + 1;
      kids.set(r.parent, n);
      out.set(r.id, `A${top.get(r.parent)}.${n} ${strip(r.title)}`);
    } else {
      top.set(r.id, top.size + 1);
      out.set(r.id, `A${top.get(r.id)} ${strip(r.title)}`);
    }
  }
  return out;
}

/** Where a locked row's lock comes from (CHANGE_plan_control). */
export const requiredBy = (plan: RunPlan) => `the ${plan.template_name ?? plan.template_id} template`;

/** Everything wrong with the plan, in the words the reviewer should read. The server says the same.
 *  `mayLift`: procurement may lift a required section with a reason; the requester driving their own run may not. */
export function validate(plan: RunPlan, mayLift = true): string[] {
  const errors: string[] = [];
  const rows = included(plan);
  const ids = new Set(rows.map((e) => e.id));
  const merged = new Set(rows.flatMap((e) => e.merged_from ?? []));
  if (rows.length > MAX_ENTRIES) errors.push(`A plan may hold at most ${MAX_ENTRIES} sections; this one has ${rows.length}.`);
  for (const e of rows) {
    if (e.is_adhoc && !(e.title ?? "").trim()) errors.push("A new section needs a title.");
    if (e.is_adhoc && !(e.merged_from ?? []).length && !(e.guidance_override ?? "").trim()) errors.push(`${e.title || "A new section"} needs instructions saying what it must cover.`);
    if ((e.focus_note ?? "").length > MAX_FOCUS_NOTE) errors.push(`The note on ${e.title} is longer than ${MAX_FOCUS_NOTE} characters.`);
    if ((e.guidance_override ?? "").length > MAX_GUIDANCE) errors.push(`The instructions for ${e.title} are longer than ${MAX_GUIDANCE} characters.`);
    if (e.parent && !ids.has(e.parent)) errors.push(`${e.title} is part of a section that is not included.`);
  }
  for (const e of rows) {
    const sources = (e.merged_from ?? []).map((id) => plan.entries.find((x) => x.id === id)).filter((x): x is RunPlanEntry => !!x);
    if (!sources.length) continue;
    if (new Set(sources.map((x) => x.parent ?? null)).size > 1) errors.push(`${e.title} can only combine sections at the same level.`);
    for (const x of sources) if (plan.entries.some((c) => c.parent === x.id && ids.has(c.id))) errors.push(`${x.title} has sub-sections in the plan; merge or leave them out first.`);
  }
  for (const e of plan.entries) {
    const lifted = !ids.has(e.id) && !!e.lifted_reason;
    if (lifted && !mayLift) errors.push(`${e.title} is required by ${requiredBy(plan)}; only procurement can lift it for a request.`);
    else if (lifted && e.lifted_reason!.trim().length < MIN_LIFT_REASON) errors.push(`Say why ${e.title} is not needed for this request (at least ${MIN_LIFT_REASON} characters).`);
    else if (e.locked === "required" && !ids.has(e.id) && !merged.has(e.id) && !lifted && (!e.parent || ids.has(e.parent))) {
      errors.push(`${e.title} is required by ${requiredBy(plan)} and cannot be dropped here; ${mayLift ? "change it under Templates, or lift it for this request with a reason" : "change it under Templates"}.`);
    }
    if (e.locked === "filtered" && ids.has(e.id)) errors.push(`${e.title} does not apply to the chosen contract model or specification approach.`);
  }
  // children directly under their parent, and parts kept together
  const order = rows.map((e) => e.id);
  rows.forEach((e, i) => {
    if (e.parent) return;
    const kids = childrenOf(plan, e.id).filter((c) => ids.has(c.id)).map((c) => c.id);
    if (kids.length && order.slice(i + 1, i + 1 + kids.length).join() !== kids.join()) errors.push(`The sub-sections of ${e.title} must stay directly under it.`);
  });
  const seen: string[] = [];
  for (const e of rows) {
    const p = e.parent ? null : partOf(e.title);
    if (!p) continue;
    if (seen[seen.length - 1] === p) continue;
    if (seen.includes(p)) { errors.push(`The sections of part ${p} must stay together.`); break; }
    seen.push(p);
  }
  return [...new Set(errors)];
}

export const toEdit = (e: RunPlanEntry): PlanEntryEdit => ({
  id: e.id, parent: e.parent ?? null, title: e.is_adhoc ? e.title : null, focus_note: e.focus_note ?? null,
  guidance_override: e.guidance_override ?? null, length: e.length ?? "medium", sme_domain: e.sme_domain ?? null,
  ...(e.merged_from?.length ? { merged_from: e.merged_from } : {}),
});

export const toResume = (plan: RunPlan, proposalIds: string[], verbosity: string | null): PlanEditResume => {
  const lifted = Object.fromEntries(plan.entries.filter((e) => !e.included && e.lifted_reason).map((e) => [e.id, e.lifted_reason!.trim()]));
  return {
    entries: included(plan).map(toEdit),
    verbosity: (verbosity as PlanEditResume["verbosity"]) ?? null,
    proposal_ids: proposalIds,
    ...(Object.keys(lifted).length ? { lifted } : {}),
  };
};

/** CHANGE_plan_control: several sections become one, in the first one's place. The server builds the combined
 *  instructions, policy text and tables; the row says what it combines and is required if any of them was. */
export function merge(plan: RunPlan, ids: string[], title: string): RunPlan {
  const rows = included(plan);
  const sources = rows.filter((e) => ids.includes(e.id));
  if (sources.length < 2 || !title.trim()) return plan;
  let id = slug(title);
  for (let n = 2; plan.entries.some((e) => e.id === id); n++) id = `${slug(title).slice(0, 44)}-${n}`;
  const entry: RunPlanEntry = {
    id, title: numberedLike(title, sources[0]!.title), parent: sources[0]!.parent ?? null, included: true, is_adhoc: true,
    locked: sources.some((e) => e.locked === "required") ? "required" : null, policy_text: sources.some((e) => e.policy_text),
    reason: `merged from ${sources.map((e) => e.title).join(", ")}`, merged_from: sources.map((e) => e.id),
    guidance: sources.map((e) => `${strip(e.title)}: ${e.guidance_override ?? e.guidance ?? ""}`).join("\n\n"), guidance_override: null,
    length: sources.some((e) => e.length === "long") ? "long" : sources.some((e) => e.length === "medium") ? "medium" : "short",
    word_target: 0, focus_note: sources.map((e) => e.focus_note).filter(Boolean).join(" ") || null, sme_domain: sources.find((e) => e.sme_domain)?.sme_domain ?? null,
  };
  const at = rows.findIndex((e) => ids.includes(e.id));
  const kept = rows.filter((e) => !ids.includes(e.id));
  const out = [...kept.slice(0, at), entry, ...kept.slice(at)];
  const left = plan.entries.filter((e) => !e.included).concat(sources.map((e) => ({ ...e, included: false, merged_into: id, reason: `merged into ${strip(entry.title)}` })));
  return { ...plan, entries: [...out, ...left] };
}

/** Undo a merge: the first part comes back in the merged section's place, each other part in its own place in the
 *  schedule (after the last row that came before it), so every part gets its original number back. */
export function split(plan: RunPlan, id: string): RunPlan {
  const rows = included(plan);
  const at = rows.findIndex((e) => e.id === id);
  const target = rows[at];
  if (!target?.merged_from?.length) return plan;
  const back = target.merged_from.map((sid) => plan.entries.find((e) => e.id === sid)).filter((e): e is RunPlanEntry => !!e)
    .map((e) => ({ ...e, included: true, merged_into: null, reason: e.reason?.startsWith("merged into") ? "restored by procurement" : e.reason }));
  if (!back.length) return plan;
  const out = [...rows.slice(0, at), back[0]!, ...rows.slice(at + 1)];
  for (const part of back.slice(1)) {
    const mine = placeOf(part.title);
    let i = out.indexOf(back[0]!);   // a part with no number goes next to the first
    if (mine) out.forEach((r, j) => { const theirs = !r.parent && !r.addendum ? placeOf(r.title) : null; if (theirs && theirs[0] === mine[0] && before(theirs, mine)) i = Math.max(i, j); });
    while (out[i + 1]?.parent && out[i + 1]!.parent === out[i]!.id) i++;   // never between a section and its sub-sections
    out.splice(i + 1, 0, part);
  }
  return { ...plan, entries: [...out, ...plan.entries.filter((e) => !e.included && !target.merged_from!.includes(e.id))] };
}

/** The plan the reviewer's list becomes when they apply a proposal: exactly what the agent described. */
export function applyResult(plan: RunPlan, result: PlanEntryEdit[]): RunPlan {
  const by = new Map(plan.entries.map((e) => [e.id, e]));
  const entries: RunPlanEntry[] = result.map((r) => {
    const known = by.get(r.id);
    const sources = (r.merged_from ?? []).map((id) => by.get(id)).filter((e): e is RunPlanEntry => !!e);
    const base: RunPlanEntry = known ?? { id: r.id, title: r.title ?? r.id, parent: r.parent ?? null, is_adhoc: true, word_target: 0, guidance: "",
      locked: sources.some((e) => e.locked === "required") ? "required" : null, policy_text: sources.some((e) => e.policy_text),
      reason: sources.length ? `merged from ${sources.map((e) => e.title).join(", ")}` : "added by the agent", merged_from: r.merged_from ?? [] };
    return { ...base, included: true, focus_note: r.focus_note ?? null, guidance_override: r.guidance_override ?? null, length: r.length ?? base.length ?? "medium", sme_domain: r.sme_domain ?? null };
  });
  const kept = new Set(entries.map((e) => e.id));
  const into = new Map(entries.flatMap((e) => (e.merged_from ?? []).map((sid) => [sid, e] as const)));
  return { ...plan, entries: [...entries, ...plan.entries.filter((e) => !kept.has(e.id)).map((e) => into.has(e.id) ? { ...e, included: false, merged_into: into.get(e.id)!.id, reason: `merged into ${strip(into.get(e.id)!.title)}` } : { ...e, included: false })] };
}

/** Move a section (with its sub-sections) after `after`, or to the front when null. Returns the plan unchanged
 *  when the move is one the rules forbid, so a drag can never leave the list in a state Accept would refuse. */
export function move(plan: RunPlan, id: string, after: string | null): RunPlan {
  const rows = included(plan);
  const block = [id, ...childrenOf(plan, id).filter((c) => c.included).map((c) => c.id)];
  const rest = rows.filter((e) => !block.includes(e.id));
  const at = after === null ? 0 : rest.findIndex((e) => e.id === after) + 1;
  if (after !== null && at === 0) return plan;
  const order = [...rest.slice(0, at).map((e) => e.id), ...block, ...rest.slice(at).map((e) => e.id)];
  const next = orderPlan(plan, order);
  const rowsNext = included(next);
  const addendaFirst = rowsNext.findIndex((e) => e.addendum);
  if (addendaFirst >= 0 && rowsNext.slice(addendaFirst).some((e) => !e.addendum)) return plan;   // addenda stay after the body
  return validate(next).length > validate(plan).length ? plan : next;
}

export function slug(title: string): string {
  const s = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 34);
  return `adhoc_${s.length >= 3 ? s : "section"}`;
}
